import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Icon, Overline, Reveal } from "../components/common";
import { ResponsiveCarousel } from "../components/ResponsiveCarousel";
import { PARTNER_CATEGORIES, IMAGES } from "../data/content";

const PartnerCard = ({ item }) => (
  <div
    data-testid={`partner-card-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 h-full flex flex-col"
  >
    <div className="flex items-center justify-between">
      <div className="h-12 w-12 rounded-xl bg-brand-mist flex items-center justify-center text-brand-navy">
        <Icon name={item.icon} className="h-6 w-6" />
      </div>
      {item.count && (
        <span className="text-2xl font-bold font-heading text-brand-teal">
          {item.count}
        </span>
      )}
    </div>
    <h3 className="mt-5 text-lg font-semibold font-heading text-brand-navy">
      {item.title}
    </h3>
    <p className="mt-2.5 text-sm text-slate-500 leading-relaxed">
      {item.description}
    </p>
  </div>
);

export default function Partners() {
  const { t } = useTranslation();
  return (
    <div data-testid="partners-page">
      {/* Hero */}
      <section className="relative pt-24 sm:pt-28 pb-8 bg-gradient-to-b from-brand-mist/60 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <Overline>{t("partners.hero.overline")}</Overline>
              <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-navy leading-[1.05]">
                {t("partners.hero.titleLead")} <span className="text-brand-teal">{t("partners.hero.titleAccent")}</span>
              </h1>
              <p className="mt-6 text-lg text-slate-500 leading-relaxed max-w-xl">
                {t("partners.hero.subtitle")}
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="relative">
                <div className="absolute -top-6 -left-6 h-40 w-40 rounded-full bg-brand-teal/15 blur-2xl" />
                <div className="relative rounded-[2rem] overflow-hidden shadow-xl ring-1 ring-black/5">
                  <img
                    src={IMAGES.partners}
                    alt="Community partners united in support"
                    className="w-full h-[360px] object-cover"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Gilead acknowledgment — repositioned above the partner grid */}
      <section className="pt-8 sm:pt-10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div
              data-testid="gilead-acknowledgment"
              className="rounded-3xl border border-gray-100 bg-brand-offwhite p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6 sm:gap-8 shadow-sm"
            >
              <img
                src="/brand/gilead-logo.png"
                alt="Gilead Sciences"
                data-testid="partner-gilead-logo"
                className="h-10 sm:h-12 w-auto object-contain shrink-0"
              />
              <div className="text-center sm:text-left">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-brand-teal">
                  {t("partners.gilead.kicker")}
                </p>
                <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">
                  {t("partners.gilead.body")}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Partner categories */}
      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl lg:max-w-none">
              <Overline>{t("partners.network.overline")}</Overline>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-brand-navy lg:whitespace-nowrap">
                {t("partners.network.title")}
              </h2>
            </div>
          </Reveal>
          <div className="mt-12">
            <ResponsiveCarousel
              items={(() => {
                const loc = t("content.partnerCategories", { returnObjects: true });
                if (!Array.isArray(loc)) return PARTNER_CATEGORIES;
                return PARTNER_CATEGORIES.map((it, i) => ({ ...it, ...(loc[i] || {}) }));
              })()}
              testId="partners-carousel"
              gridCols="md:grid-cols-2 lg:grid-cols-4"
              renderItem={(item) => <PartnerCard item={item} />}
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 bg-brand-offwhite">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Overline>{t("partners.cta.overline")}</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-brand-navy">
              {t("partners.cta.title")}
            </h2>
            <p className="mt-4 text-slate-500 leading-relaxed max-w-xl mx-auto">
              {t("partners.cta.body")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
              <Link
                to="/contact"
                data-testid="partners-contact-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white px-8 py-3.5 font-semibold transition-colors w-full sm:w-auto"
              >
                {t("partners.cta.cta")}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="/downloads/hepcure-flyer-patient-color.pdf"
                download="hepcure-flyer-patient-color.pdf"
                data-testid="partners-download-flyer"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white px-8 py-3 font-semibold transition-colors w-full sm:w-auto"
              >
                {t("partners.cta.downloadFlyer")}
                <Download className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
